/**
 * PDF Export Utility for ZapLens
 * Generates professional PDF documentation of Zapier workflows
 * Uses jsPDF for PDF generation and html2canvas for graph screenshots
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ParsedWorkflow } from '../types/zapier';

export interface ExportOptions {
  /** Include locked/premium fields in export */
  includeLocked: boolean;
  /** Add branding/watermark */
  addBranding: boolean;
  /** Custom consultant/company name for branding */
  brandingText?: string;
  /** Page orientation */
  orientation?: 'portrait' | 'landscape';
}

/**
 * Captures the React Flow graph as a high-quality image
 * Automatically fits all nodes in view before capturing
 *
 * @param nodeCount - Number of nodes in the workflow (for scaling calculation)
 * @returns Base64 encoded PNG image
 */
export async function captureWorkflowGraph(nodeCount: number = 1): Promise<string> {
  const graphElement = document.querySelector('.react-flow') as HTMLElement;

  if (!graphElement) {
    throw new Error('Workflow graph not found');
  }

  try {
    // Get the React Flow instance to call fitView
    // We'll use a custom event to communicate with the component
    const fitViewPromise = new Promise<void>((resolve) => {
      const handler = () => {
        document.removeEventListener('pdf-fit-view-complete', handler);
        resolve();
      };
      document.addEventListener('pdf-fit-view-complete', handler);

      // Dispatch event to trigger fitView in the React component
      document.dispatchEvent(new CustomEvent('pdf-trigger-fit-view', {
        detail: { duration: 0 } // Instant fit, no animation
      }));
    });

    // Wait for fit view to complete (with timeout)
    await Promise.race([
      fitViewPromise,
      new Promise(resolve => setTimeout(resolve, 1000)) // 1s timeout
    ]);

    // Calculate scale based on node count for better quality
    // More nodes = need higher resolution to keep them readable
    const baseScale = 2;
    const scaleMultiplier = nodeCount > 15 ? 1.5 : nodeCount > 8 ? 1.25 : 1;
    const scale = baseScale * scaleMultiplier;

    const canvas = await html2canvas(graphElement, {
      backgroundColor: '#ffffff',
      scale,
      logging: false,
      useCORS: true,
      // Capture larger area for complex workflows
      windowWidth: Math.max(graphElement.scrollWidth, 1920),
      windowHeight: Math.max(graphElement.scrollHeight, 1080),
    });

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to capture workflow graph:', error);
    throw new Error('Could not capture workflow visualization');
  }
}

/**
 * Generates a comprehensive PDF document for a workflow
 */
export async function generateWorkflowPDF(
  workflow: ParsedWorkflow,
  options: ExportOptions = { includeLocked: false, addBranding: true }
): Promise<void> {
  const doc = new jsPDF({
    orientation: options.orientation || 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  // ====================
  // COVER PAGE
  // ====================

  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Workflow Documentation', margin, yPosition);
  yPosition += 10;

  // Workflow Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.text(workflow.title || 'Untitled Workflow', margin, yPosition);
  yPosition += 8;

  // Metadata Line
  doc.setFontSize(10);
  doc.setTextColor(100);
  const exportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  doc.text(`Exported: ${exportDate}`, margin, yPosition);
  yPosition += 5;

  if (workflow.status) {
    const statusText = `Status: ${workflow.status === 'on' ? 'Active' : 'Inactive'}`;
    doc.text(statusText, margin, yPosition);
    yPosition += 5;
  }

  doc.text(`Steps: ${workflow.nodes.length}`, margin, yPosition);
  yPosition += 15;

  // Separator Line
  doc.setDrawColor(200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Description (if available)
  if (workflow.description) {
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'normal');

    const descLines = doc.splitTextToSize(workflow.description, contentWidth);
    doc.text(descLines, margin, yPosition);
    yPosition += descLines.length * 5 + 10;
  }

  // ====================
  // WORKFLOW VISUALIZATION
  // ====================

  yPosition += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('Workflow Visualization', margin, yPosition);
  yPosition += 8;

  try {
    const graphImage = await captureWorkflowGraph(workflow.nodes.length);

    // Calculate image dimensions to fit page
    const maxImageHeight = pageHeight - yPosition - margin - 20;
    const maxImageWidth = contentWidth;

    // Add image (centered)
    const imageWidth = maxImageWidth;
    const imageHeight = Math.min(maxImageHeight, imageWidth * 0.75); // Maintain aspect ratio

    doc.addImage(
      graphImage,
      'PNG',
      margin,
      yPosition,
      imageWidth,
      imageHeight,
      undefined,
      'FAST'
    );

    yPosition += imageHeight + 10;
  } catch (error) {
    console.warn('Could not add workflow graph to PDF:', error);
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text('(Workflow visualization unavailable)', margin, yPosition);
    yPosition += 10;
  }

  // ====================
  // STEP DETAILS TABLE
  // ====================

  doc.addPage();
  yPosition = margin;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('Step Details', margin, yPosition);
  yPosition += 10;

  // Table setup
  const rowHeight = 7;
  const col1Width = 15;  // Step #
  const col2Width = 35;  // App
  const col3Width = 45;  // Action

  // Table header
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPosition, contentWidth, rowHeight, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('#', margin + 2, yPosition + 5);
  doc.text('App', margin + col1Width + 2, yPosition + 5);
  doc.text('Action', margin + col1Width + col2Width + 2, yPosition + 5);
  doc.text('Type', margin + col1Width + col2Width + col3Width + 2, yPosition + 5);

  yPosition += rowHeight;

  // Table rows
  workflow.nodes.forEach((node, index) => {
    // Check if we need a new page
    if (yPosition > pageHeight - margin - 20) {
      doc.addPage();
      yPosition = margin;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(0);

    // Step number
    doc.text(`${index + 1}`, margin + 2, yPosition + 5);

    // App name (without emoji - jsPDF has limited Unicode support)
    doc.text(node.data.app, margin + col1Width + 2, yPosition + 5);

    // Action (truncated if too long)
    const actionText = doc.splitTextToSize(node.data.event, col3Width - 4);
    doc.text(actionText[0], margin + col1Width + col2Width + 2, yPosition + 5);

    // Type badge
    const typeText = node.data.nodeType.toUpperCase();
    doc.setTextColor(100);
    doc.text(typeText, margin + col1Width + col2Width + col3Width + 2, yPosition + 5);
    doc.setTextColor(0);

    // Separator line
    doc.setDrawColor(230);
    doc.line(margin, yPosition + rowHeight, pageWidth - margin, yPosition + rowHeight);

    yPosition += rowHeight;

    // Configuration details (if unlocked or included)
    if (options.includeLocked && node.data.config && Object.keys(node.data.config).length > 0) {
      yPosition += 3;
      doc.setFontSize(8);
      doc.setTextColor(80);
      doc.setFont('helvetica', 'italic');

      const configSummary = `Configuration: ${Object.keys(node.data.config).length} fields`;
      doc.text(configSummary, margin + col1Width + 2, yPosition + 2);

      yPosition += 5;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0);
    }
  });

  // ====================
  // FOOTER / BRANDING
  // ====================

  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Page number
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );

    // Branding
    if (options.addBranding) {
      const brandText = options.brandingText || 'Generated with ZapLens';
      doc.setFontSize(7);
      doc.text(
        brandText,
        pageWidth - margin,
        pageHeight - 10,
        { align: 'right' }
      );

      // Watermark for free exports (if not unlocked)
      if (!options.includeLocked && i === 1) {
        doc.setFontSize(10);
        doc.setTextColor(220, 220, 220);
        doc.text(
          'Exported with Free Version - Upgrade for full configuration details',
          pageWidth / 2,
          pageHeight - 20,
          { align: 'center' }
        );
      }
    }
  }

  // ====================
  // SAVE PDF
  // ====================

  const filename = `${workflow.title || 'workflow'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}

/**
 * Quick export function for free users (limited features)
 */
export async function exportWorkflowBasic(workflow: ParsedWorkflow): Promise<void> {
  await generateWorkflowPDF(workflow, {
    includeLocked: false,
    addBranding: true,
    orientation: 'portrait',
  });
}

/**
 * Full export function for premium users (all features)
 */
export async function exportWorkflowPremium(
  workflow: ParsedWorkflow,
  customBranding?: string
): Promise<void> {
  await generateWorkflowPDF(workflow, {
    includeLocked: true,
    addBranding: true,
    brandingText: customBranding,
    orientation: 'portrait',
  });
}
